import { useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useListing } from "@/hooks/useListing";
import { UserType } from "@/types/user";
import type { ListingMyListings } from "@/types/listing";
import { useSettings } from "@/hooks/useSettings";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";

const RELEVANT_STATUSES = new Set(["active", "reserved", "completed"]);

/**
 * Listing från fordonstest: primärt API-fält, annars heuristik så påminnelsen fungerar
 * även om äldre svar saknar `carTestId` (my-listings miniatyr hämtas då bara från carTest-bilder).
 */
function isFromVehicleTestListing(listing: ListingMyListings): boolean {
  if (listing.fromVehicleTest === true) return true;
  if (listing.carTestId != null && String(listing.carTestId).trim() !== "") return true;
  if (listing.thumbnail?.trim()) return true;
  return false;
}

function listingNeedsDeliveryReminder(listing: ListingMyListings): boolean {
  if (!RELEVANT_STATUSES.has(listing.status)) return false;
  return isFromVehicleTestListing(listing);
}

/**
 * Dashboard notice for sellers with a listing from a vehicle test (active / reserved / sold)
 * who have not saved any delivery address yet.
 */
export function DeliveryAddressReminderBanner() {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { user } = useAuth();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.deliveryAddressReminder;
  const { getMyListings } = useListing();

  // All seller-facing account types (inkl. UserType.User för äldre API-svar)
  const enabled = !!user && user.type !== UserType.Admin;

  const myListingsQuery = getMyListings(
    { page: 1, limit: 50 },
    { enabled }
  );

  const {
    deliveryAddresses,
    isLoadingDeliveryAddresses,
    deliveryAddressesError,
  } = useSettings({ enabledDeliveryAddresses: enabled });

  const show = useMemo(() => {
    if (!enabled || !labels) return false;
    if (myListingsQuery.isLoading || isLoadingDeliveryAddresses) return false;
    if (myListingsQuery.isError || deliveryAddressesError) return false;
    const addresses = deliveryAddresses ?? [];
    if (addresses.length > 0) return false;
    const listings = myListingsQuery.data?.listings ?? [];
    return listings.some(listingNeedsDeliveryReminder);
  }, [
    enabled,
    labels,
    myListingsQuery.isLoading,
    myListingsQuery.isError,
    myListingsQuery.data?.listings,
    isLoadingDeliveryAddresses,
    deliveryAddressesError,
    deliveryAddresses,
  ]);

  if (!show || !labels) {
    return null;
  }

  return (
    <div
      className={cn(
        "rounded-lg border border-amber-500/50 bg-amber-500/10 dark:bg-amber-500/15 mb-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 native-dashboard-banner",
        isNative ? "px-4 py-3.5" : "px-3 py-2.5 sm:px-4 sm:py-3",
        isMobile ? "w-full" : "w-[85%] mx-auto",
      )}
      role="status"
    >
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <MapPin
          className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400 mt-0.5"
          aria-hidden
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{labels.title}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5">
            {labels.description}
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" className="shrink-0 w-full sm:w-auto" asChild>
        <Link to="/settings?tab=delivery">{labels.cta}</Link>
      </Button>
    </div>
  );
}
